import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import DistributionHubService from 'service/DistributionHubService';
import { DistributionHubDetailResp } from 'domain/payload/response/distributionHub/DistributionHubDetailResp';

export interface DistributionHubDetailModalProps {
    show?: boolean;
    hubId?: string | null;
    initialData?: DistributionHubDetailResp | null;
    onClose: () => void;
    onEdit?: (id: string) => void;
    asModal?: boolean;
    className?: string;
}

const DistributionHubDetailModal: React.FC<DistributionHubDetailModalProps> = ({
    show = true,
    hubId,
    initialData = null,
    onClose,
    onEdit,
    asModal = false,
    className = ''
}) => {
    const { profile } = useSelector((state: any) => state.auth);
    const token = profile?.data?.token;

    const [data, setData] = useState<DistributionHubDetailResp | null>(initialData);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    const distributionHubService = useMemo(() => DistributionHubService(), []);

    useEffect(() => {
        if (!show) return;

        if (initialData && (!hubId || initialData.id === hubId)) {
            setData(initialData);
            return;
        }

        if (!hubId) {
            setData(null);
            return;
        }

        if (!token) {
            setError('Token tidak ditemukan. Silakan login kembali.');
            return;
        }

        let isMounted = true;
        const fetchDetail = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await distributionHubService.distributionHubDetail(token, hubId);
                if (isMounted) {
                    setData(res.data ?? null);
                }
            } catch (err: any) {
                if (isMounted) {
                    console.error('Failed to fetch distribution hub detail:', err);
                    const errMsg = typeof err === 'string' ? err : err?.message || 'Gagal memuat detail Distribution Hub';
                    setError(errMsg);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchDetail();

        return () => {
            isMounted = false;
        };
    }, [hubId, initialData, token, show, distributionHubService]);

    const formatWhatsAppUrl = (phone?: string) => {
        if (!phone) return '#';
        let cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('0')) {
            cleaned = '62' + cleaned.substring(1);
        } else if (cleaned.startsWith('8')) {
            cleaned = '62' + cleaned;
        }
        return `https://wa.me/${cleaned}`;
    };

    const formatCoordinates = (lat?: number, lng?: number) => {
        if (lat === undefined || lng === undefined) return '-';
        return `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
    };

    const handleCopyCoordinates = async () => {
        if (!data || data.latitude === undefined || data.longitude === undefined) return;
        const textToCopy = `${data.latitude}, ${data.longitude}`;
        try {
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy coordinates to clipboard:', err);
        }
    };

    if (!show || (!hubId && !data && !loading && !error)) {
        return null;
    }

    const renderCardBody = () => {
        if (loading) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted small mt-2 mb-0">Memuat detail Distribution Hub...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className="alert alert-danger my-3" role="alert">
                    <div className="d-flex align-items-center">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        <span>{error}</span>
                    </div>
                </div>
            );
        }

        if (!data) {
            return (
                <div className="text-center text-muted py-4">
                    Data tidak ditemukan
                </div>
            );
        }

        const cityProvince = [data.city, data.province].filter(Boolean).join(', ');
        const districtSub = [
            data.district ? `Kec. ${data.district}` : '',
            data.subdistrict ? `Kel. ${data.subdistrict}` : ''
        ].filter(Boolean).join(', ');

        return (
            <div className="d-flex flex-column gap-3">
                {/* ══ SECTION 1: PERSON IN CHARGE (PIC) ══ */}
                <div
                    className="border rounded-3 p-3 bg-white"
                    style={{ borderColor: '#e2e8f0' }}
                >
                    <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-user me-2 text-primary" style={{ fontSize: '13px' }}></i>
                        <span
                            className="fw-bold text-primary text-uppercase"
                            style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                        >
                            PERSON IN CHARGE (PIC)
                        </span>
                    </div>

                    <div className="d-flex flex-column gap-2">
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Name</div>
                            <div className="col-8 fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                {data.picName || '-'}
                            </div>
                        </div>
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Phone</div>
                            <div className="col-8" style={{ fontSize: '13px' }}>
                                {data.picPhone ? (
                                    <a
                                        href={formatWhatsAppUrl(data.picPhone)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary text-decoration-none fw-semibold d-inline-flex align-items-center"
                                        title="Chat via WhatsApp"
                                    >
                                        <span>{data.picPhone}</span>
                                        <i
                                            className="fa-brands fa-whatsapp text-success ms-2"
                                        ></i>
                                    </a>
                                ) : (
                                    '-'
                                )}
                            </div>
                        </div>
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Email</div>
                            <div
                                className="col-8 text-dark"
                                style={{ fontSize: '13px', wordBreak: 'break-all' }}
                            >
                                {data.picEmail || '-'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ══ SECTION 2: ADDRESS & COORDINATES ══ */}
                <div
                    className="border rounded-3 p-3 bg-white"
                    style={{ borderColor: '#e2e8f0' }}
                >
                    <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-map-marker me-2 text-primary" style={{ fontSize: '14px' }}></i>
                        <span
                            className="fw-bold text-primary text-uppercase"
                            style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                        >
                            ADDRESS & COORDINATES
                        </span>
                    </div>

                    <div className="d-flex flex-column gap-2">
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Region</div>
                            <div className="col-8 fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                {data.region || '-'}
                            </div>
                        </div>
                        <div className="row g-0 align-items-start">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Address</div>
                            <div className="col-8 text-dark" style={{ fontSize: '13px' }}>
                                {data.address || '-'}
                            </div>
                        </div>
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>City / Province</div>
                            <div className="col-8 text-dark" style={{ fontSize: '13px' }}>
                                {cityProvince || '-'}
                            </div>
                        </div>
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>District / Sub.</div>
                            <div className="col-8 text-dark" style={{ fontSize: '13px' }}>
                                {districtSub || '-'}
                            </div>
                        </div>
                        <div className="row g-0 align-items-center">
                            <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Coordinates</div>
                            <div className="col-8 d-flex align-items-center flex-wrap gap-2" style={{ fontSize: '13px' }}>
                                <span className="text-dark">
                                    {formatCoordinates(data.latitude, data.longitude)}
                                </span>
                                {(data.latitude !== 0 || data.longitude !== 0) && (
                                    <div className="d-inline-flex align-items-center gap-1">
                                        <button
                                            type="button"
                                            className="btn btn-small-compact btn-outline-secondary"
                                            onClick={handleCopyCoordinates}
                                            title="Copy coordinates"
                                        >
                                            <div className='d-inline-flex align-items-center px-1'>
                                                <i className={`fa ${copied ? 'fa-check text-success' : 'fa-copy'} me-1`}></i>
                                                {copied ? 'Copied' : 'Copy'}
                                            </div>
                                        </button>
                                        <a
                                            href={`https://www.google.com/maps?q=${data.latitude},${data.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-small-compact btn-outline-primary text-decoration-none"
                                            title="Buka di Google Maps"
                                        >
                                            <div className='d-inline-flex align-items-center px-1 py-1'>
                                                <i className="fa fa-external-link me-1"></i>
                                                Maps
                                            </div>
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ══ SECTION 3: MORE ACCESS AFFILIATION ══ */}
                {data.moreAccessAffiliated ? (
                    <div
                        className="rounded-3 p-3"
                        style={{
                            backgroundColor: '#edfbf3',
                            border: '1px solid #bbf0d0'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center">
                                <i className="fa fa-link me-2 text-primary" style={{ fontSize: '13px' }}></i>
                                <span
                                    className="fw-bold text-primary text-uppercase"
                                    style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                                >
                                    MORE ACCESS AFFILIATION
                                </span>
                            </div>
                            <span
                                className="badge rounded-pill d-inline-flex align-items-center"
                                style={{
                                    backgroundColor: '#166534',
                                    color: '#ffffff',
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    fontWeight: 500
                                }}
                            >
                                <i className="fa fa-check me-1" style={{ fontSize: '10px' }}></i>
                                Connected
                            </span>
                        </div>

                        <div className="d-flex flex-column gap-2">
                            <div className="row g-0 align-items-center">
                                <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Name</div>
                                <div className="col-8 fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                    {data.picName || '-'}
                                </div>
                            </div>
                            <div className="row g-0 align-items-center">
                                <div className="col-4 text-muted" style={{ fontSize: '13px' }}>Email</div>
                                <div
                                    className="col-8 text-dark"
                                    style={{ fontSize: '13px', wordBreak: 'break-all' }}
                                >
                                    {data.picEmail || '-'}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className="rounded-3 p-3"
                        style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #e2e8f0'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center">
                                <i className="fa fa-link me-2 text-secondary" style={{ fontSize: '13px' }}></i>
                                <span
                                    className="fw-bold text-secondary text-uppercase"
                                    style={{ fontSize: '11.5px', letterSpacing: '0.5px' }}
                                >
                                    MORE ACCESS AFFILIATION
                                </span>
                            </div>
                            <span
                                className="badge rounded-pill"
                                style={{
                                    backgroundColor: '#4b5563',
                                    color: '#ffffff',
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    fontWeight: 500
                                }}
                            >
                                Not Connected
                            </span>
                        </div>
                        <p className="text-secondary small mb-0" style={{ fontSize: '12.5px' }}>
                            Not affiliated with a MORE ACCESS account.
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const cardContent = (
        <div className="d-flex flex-column h-100">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h5 className="fw-bold text-dark mb-0 pe-2" style={{ fontSize: '17px' }}>
                    {data?.name || 'Detail Distribution Hub'}
                </h5>
                <button
                    type="button"
                    className="btn btn-link text-muted p-0 text-decoration-none"
                    onClick={onClose}
                    aria-label="Close"
                    style={{ fontSize: '16px', lineHeight: 1 }}
                >
                    <i className="fa fa-times"></i>
                </button>
            </div>

            {/* Body */}
            <div className="flex-grow-1 overflow-auto pe-1">
                {renderCardBody()}
            </div>

            {/* Footer */}
            {data && !loading && (
                <div className="d-flex justify-content-end align-items-center gap-2 mt-4 pt-2">
                    <button
                        type="button"
                        className="btn btn-outline-secondary px-3 py-1"
                        style={{ fontSize: '13px' }}
                        onClick={onClose}
                    >
                        Close
                    </button>
                    <Link
                        to={`/distribution-hub/${data.id}`}
                        className="btn btn-primary px-3 py-1 d-inline-flex align-items-center text-white text-decoration-none"
                        style={{ fontSize: '13px' }}
                        onClick={() => onEdit?.(data.id)}
                    >
                        <i className="fa fa-pencil-square-o me-2" style={{ fontSize: '14px' }}></i>
                        Edit Hub
                    </Link>
                </div>
            )}
        </div>
    );

    if (asModal) {
        return (
            <div
                className="modal fade show"
                style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
                onClick={onClose}
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content border-0 p-4 rounded-4 shadow">
                        {cardContent}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`card border rounded-3 shadow-sm p-4 bg-white ${className}`}
            style={{ borderColor: '#e5e7eb', minWidth: '360px' }}
        >
            {cardContent}
        </div>
    );
};

export default DistributionHubDetailModal;
